import React from "react";
import * as XLSX from "xlsx-js-style";
import { IBranch } from "../../models/Branch";
import {
  findPayrollSummary,
  findPayrollRowDetailsBatch,
  type PayrollRowDetailsSnapshot,
  isPayrollFetchAborted,
  findPayrollDenominationBreakdown,
  upsertPayrollDenominationBreakdown,
} from "../../services/payroll.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { getCurrencyFormat } from "../../utils/index.ts";
import dayjs from "dayjs";
import { computePartnerPayBreakdownFromDetail } from "../../utils/payrollUtils.ts";
import { mergeDailyPaymentsByDate } from "../../utils/payrollVisitHours.ts";
import { readFullDayHoursByBranch } from "../../services/branchSchedule.service.ts";
import EmptyImage from "../../components/EmptyImage.tsx";
import ModalContainer from "../../components/ModalContainer.tsx";

const DENOMINATIONS = [500, 200, 100, 50, 20, 10, 5, 2, 1, 0.5] as const;

interface CollaboratorTotal {
  partnerId: number;
  name: string;
  totalPayWithIncentives: number;
}
type DenominationCounts = Record<string, number>;

const toDenominationKey = (value: number): string => {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, "");
};

const toDisplayName = (name: string): string =>
  (name || "")
    .trim()
    .split(/\s+/)
    .map((word) => {
      const lower = word.toLocaleLowerCase("es");
      const firstChar = lower.charAt(0).toLocaleUpperCase("es");
      return `${firstChar}${lower.slice(1)}`;
    })
    .join(" ");

const normalizeSearchText = (value: string): string =>
  (value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const calculateDenominationBreakdown = (amount: number) => {
  const roundedAmount = Math.max(0, Math.round(amount * 2) / 2);
  let remainingCents = Math.round(roundedAmount * 100);

  const counts: Record<string, number> = {};
  DENOMINATIONS.forEach((denomination) => {
    const key = toDenominationKey(denomination);
    const denominationCents = Math.round(denomination * 100);
    const count = Math.floor(remainingCents / denominationCents);
    counts[key] = count;
    remainingCents -= count * denominationCents;
  });

  return {
    roundedAmount,
    counts,
  };
};

const calculateAssignedAmount = (counts: DenominationCounts): number =>
  DENOMINATIONS.reduce((sum, denomination) => {
    const key = toDenominationKey(denomination);
    return sum + (counts[key] || 0) * denomination;
  }, 0);

const normalizeCountInput = (raw: string): string => {
  const digitsOnly = (raw || "").replace(/\D/g, "");
  if (!digitsOnly) return "0";
  return digitsOnly.replace(/^0+(?=\d)/, "");
};

const categorizePayrollDeductions = (
  record: any,
  totalDeductionsNumber: number
) => {
  let totalAuditoria = 0;
  let totalPrestamos = 0;
  let totalOtros = 0;

  const classify = (deduccion: any) => {
    if (!(deduccion.calculatedAmount > 0)) return;
    const nameLower = (deduccion.name || "").toLowerCase();
    if (
      nameLower.includes("semáforo") ||
      nameLower.includes("semáforo laboral") ||
      nameLower.includes("auditoría") ||
      nameLower.includes("auditoria")
    ) {
      totalAuditoria += deduccion.calculatedAmount;
    } else if (
      nameLower.includes("préstamo") ||
      nameLower.includes("prestamo") ||
      nameLower.includes("préstamos") ||
      nameLower.includes("prestamos")
    ) {
      totalPrestamos += deduccion.calculatedAmount;
    } else if (
      !nameLower.includes("faltante") &&
      !nameLower.includes("caja")
    ) {
      totalOtros += deduccion.calculatedAmount;
    }
  };

  (record.calculatedMissingBoxes || []).forEach(classify);
  (record.calculatedNewDeductions || []).forEach((deduccion: any) => {
    if (!(deduccion.calculatedAmount > 0)) return;
    const nameLower = (deduccion.name || "").toLowerCase();
    if (
      nameLower.includes("semáforo") ||
      nameLower.includes("semáforo laboral") ||
      nameLower.includes("auditoría") ||
      nameLower.includes("auditoria")
    ) {
      totalAuditoria += deduccion.calculatedAmount;
    } else if (
      nameLower.includes("préstamo") ||
      nameLower.includes("prestamo") ||
      nameLower.includes("préstamos") ||
      nameLower.includes("prestamos")
    ) {
      totalPrestamos += deduccion.calculatedAmount;
    } else {
      totalOtros += deduccion.calculatedAmount;
    }
  });

  if (
    totalDeductionsNumber > 0 &&
    totalAuditoria + totalPrestamos + totalOtros === 0
  ) {
    totalOtros = totalDeductionsNumber;
  }

  return { totalAuditoria, totalPrestamos, totalOtros };
};

const emptyPartnerBranchCalc = (record: any) => ({
  totalDailyPayments: 0,
  totalIncentives: 0,
  totalDeductions: 0,
  totalMissingBoxDeductions: 0,
  uniformDeduction: 0,
  totalAuditoria: 0,
  totalPrestamos: 0,
  totalOtros: 0,
  totalPayWithIncentives: 0,
  collaborator: {
    partnerId: Number(record.partnerId || 0),
    name: record.name || "Sin nombre",
    totalPayWithIncentives: 0,
  },
});

const calcPartnerTotalFromSnapshot = (
  record: any,
  snap: PayrollRowDetailsSnapshot | undefined,
  fullDayHours: number
) => {
  if (!snap?.paymentDetail) {
    return emptyPartnerBranchCalc(record);
  }

  const payment = snap.paymentDetail as Record<string, any>;
  const chart = snap.chartDaysWorked as Record<string, any> | undefined;

  const breakdown = computePartnerPayBreakdownFromDetail({
    paymentDetail: {
      dailyPayments: mergeDailyPaymentsByDate(payment.dailyPayments || []),
      freeDaysEnjoyedDates: payment.freeDaysEnjoyedDates || [],
    },
    chartFreeDaysDates: chart?.freeDaysDates,
    savedIncidents: chart?.savedIncidents,
    record,
    fullDayHours,
  });

  if (!breakdown) {
    return emptyPartnerBranchCalc(record);
  }

  const { totalAuditoria, totalPrestamos, totalOtros } = categorizePayrollDeductions(
    record,
    breakdown.totalDeductions
  );

  return {
    totalDailyPayments: breakdown.totalDailyPayments,
    totalIncentives: breakdown.totalIncentives,
    totalDeductions: breakdown.totalDeductions,
    totalMissingBoxDeductions: breakdown.totalMissingBoxDeductions,
    uniformDeduction: breakdown.uniformDeduction,
    totalAuditoria,
    totalPrestamos,
    totalOtros,
    totalPayWithIncentives: breakdown.totalPayWithIncentives,
    collaborator: {
      partnerId: Number(record.partnerId || 0),
      name: record.name || "Sin nombre",
      totalPayWithIncentives: breakdown.totalPayWithIncentives,
    },
  };
};

async function resolveFullDayHoursForBranch(branchId: number): Promise<number> {
  try {
    const response = await readFullDayHoursByBranch(branchId);
    if (response?.fullDayHours !== undefined && response?.fullDayHours !== null) {
      const parsed =
        typeof response.fullDayHours === "string"
          ? parseFloat(response.fullDayHours)
          : Number(response.fullDayHours);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
  } catch {
    /* default below */
  }
  return 8.5;
}

interface BranchTotal {
  branchId: number;
  branchName: string;
  totalDailyPayments: number;
  totalIncentives: number;
  totalDeductions: number;
  totalMissingBoxDeductions: number;
  uniformDeduction: number;
  totalAuditoria: number;
  totalPrestamos: number;
  totalOtros: number;
  totalPayWithIncentives: number;
  totalCollaborators: number;
  collaboratorTotals: CollaboratorTotal[];
  loading: boolean;
}

const PaymentEstimadoTotal = ({
  selectedBranch,
  dates,
  onExportExcel,
}: {
  selectedBranch: IBranch[];
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  onExportExcel?: (exportFn: () => void) => void;
}) => {
  const [branchesTotals, setBranchesTotals] = React.useState<
    Map<number, BranchTotal>
  >(new Map());
  const [loadingBranches, setLoadingBranches] = React.useState<Set<number>>(new Set());
  const [selectedBranchBreakdown, setSelectedBranchBreakdown] = React.useState<number | null>(null);
  const [denominationSearch, setDenominationSearch] = React.useState("");
  const [editableCountsByPartner, setEditableCountsByPartner] = React.useState<
    Map<number, DenominationCounts>
  >(new Map());
  const [lastEditedFieldByPartner, setLastEditedFieldByPartner] = React.useState<
    Map<number, string>
  >(new Map());
  const [dirtyPartnerIds, setDirtyPartnerIds] = React.useState<Set<number>>(new Set());
  const [isSavingAllEdits, setIsSavingAllEdits] = React.useState(false);
  const lastDatesRef = React.useRef<string>("");

  // Misma jornada que Cuadro de Mando: horas por sucursal (no las de la primera del listado).
  const calculateBranchTotal = async (
    branch: IBranch,
    signal?: AbortSignal
  ): Promise<BranchTotal> => {
    try {
      const fullDayHours = await resolveFullDayHoursForBranch(Number(branch.id));
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      // Fetch de todos los records de la sucursal
      // Primero obtener el total para saber cuántos records hay
      const summaryResponse = await findPayrollSummary(
        Number(branch.id),
        startDate,
        endDate,
        10000, // Limite muy alto para obtener todos los records
        0,
        "",
        "Chilito",
        "asc",
        signal
      );

      const allRecords = summaryResponse?.payroll || [];

      if (allRecords.length === 0) {
        console.log(`No hay records para la sucursal ${branch.name}`);
        return {
          branchId: Number(branch.id),
          branchName: branch.name,
          totalDailyPayments: 0,
          totalIncentives: 0,
          totalDeductions: 0,
          totalMissingBoxDeductions: 0,
          uniformDeduction: 0,
          totalAuditoria: 0,
          totalPrestamos: 0,
          totalOtros: 0,
          totalPayWithIncentives: 0,
          totalCollaborators: 0,
          collaboratorTotals: [],
          loading: false,
        };
      }

      // Mapear los records para que tengan el mismo formato que en Redux (EXACTAMENTE igual que PayrollBranch.tsx líneas 291-343)
      // Esto asegura que el parseo sea EXACTAMENTE igual que en CuadroMando.tsx
      const mappedRecords = allRecords.map((record: any) => ({
        ...record,
        // El backend devuelve dailySalary, hourlyPay, etc. como números
        // Mapear EXACTAMENTE igual que PayrollBranch.tsx línea 298-299
        dailySalary: `$${(typeof record.dailySalary === "number" ? record.dailySalary : Number(record.dailySalary || 0)).toFixed(2)}`,
        payPerHour: `$${(typeof record.hourlyPay === "number" ? record.hourlyPay : Number(record.hourlyPay || record.payPerHour || 0)).toFixed(2)}`,
        // Mapear igual que PayrollBranch.tsx líneas 307-308
        totalIncentives: `$${(typeof record.totalIncentives === "number" ? record.totalIncentives : Number(record.totalIncentives || 0)).toFixed(2)}`,
        totalDeductions: `$${(typeof record.totalDeductions === "number" ? record.totalDeductions : Number(record.totalDeductions || 0)).toFixed(2)}`,
        // Mapear uniformDeduction igual que PayrollBranch.tsx líneas 321-323
        uniformDeduction: record.uniformDeduction
          ? `$${(typeof record.uniformDeduction === "number" ? record.uniformDeduction : Number(record.uniformDeduction || 0)).toFixed(2)}`
          : undefined,
        totalPayWithIncentives: `$${(typeof record.totalPayWithIncentives === "number" ? record.totalPayWithIncentives : Number(record.totalPayWithIncentives || 0)).toFixed(2)}`,
      }));

      console.log(`Procesando ${mappedRecords.length} colaboradores para ${branch.name}`);
      // Log del primer record para verificar estructura
      if (mappedRecords.length > 0) {
        console.log("Ejemplo de record mapeado:", {
          partnerId: mappedRecords[0].partnerId,
          name: mappedRecords[0].name,
          dailySalary: mappedRecords[0].dailySalary,
          payPerHour: mappedRecords[0].payPerHour,
          totalIncentives: mappedRecords[0].totalIncentives,
          totalDeductions: mappedRecords[0].totalDeductions,
        });
      }

      let totalDailyPaymentsSum = 0;
      let totalIncentivesSum = 0;
      let totalDeductionsSum = 0;
      let totalMissingBoxDeductionsSum = 0;
      let uniformDeductionSum = 0;
      let totalAuditoriaSum = 0;
      let totalPrestamosSum = 0;
      let totalOtrosSum = 0;

      const partnerIds = mappedRecords
        .map((r: any) => Number(r.partnerId))
        .filter((id: number) => Number.isFinite(id) && id > 0);

      const rowDetailsByPartner: Record<number, PayrollRowDetailsSnapshot> = {};
      mappedRecords.forEach((record: any) => {
        const embedded = record.snapshotDetail as
          | PayrollRowDetailsSnapshot
          | undefined;
        if (embedded?.paymentDetail && embedded?.chartDaysWorked) {
          rowDetailsByPartner[record.partnerId] = embedded;
        }
      });

      const missingPartnerIds = partnerIds.filter((id) => !rowDetailsByPartner[id]);
      if (missingPartnerIds.length > 0) {
        const batchMap = await findPayrollRowDetailsBatch(
                Number(branch.id),
                startDate,
                endDate,
          missingPartnerIds,
                signal
        );
        Object.assign(rowDetailsByPartner, batchMap);
      }

      const calculations = mappedRecords.map((record: any) =>
        calcPartnerTotalFromSnapshot(
          record,
          rowDetailsByPartner[record.partnerId],
          fullDayHours
        )
      );

      // Sumar todos los colaboradores (incluye total 0 o negativo; misma regla que Cuadro de Mando)
      let totalPayWithIncentivesSum = 0;
      const collaboratorTotals: CollaboratorTotal[] = [];

      calculations.forEach((calc) => {
        if (!calc) {
          return;
        }
        totalDailyPaymentsSum += calc.totalDailyPayments || 0;
        totalIncentivesSum += calc.totalIncentives || 0;
        totalDeductionsSum += calc.totalDeductions || 0;
        totalMissingBoxDeductionsSum += calc.totalMissingBoxDeductions || 0;
        uniformDeductionSum += calc.uniformDeduction || 0;
        totalAuditoriaSum += calc.totalAuditoria || 0;
        totalPrestamosSum += calc.totalPrestamos || 0;
        totalOtrosSum += calc.totalOtros || 0;
        totalPayWithIncentivesSum += calc.totalPayWithIncentives || 0;
        if (calc.collaborator) {
          collaboratorTotals.push(calc.collaborator);
        }
      });
      
      // NOTA: En CuadroMando.tsx también se calcula sumando los componentes al final,
      // pero aquí usamos totalPayWithIncentivesSum que es la suma directa de los totales individuales
      // para que sea EXACTAMENTE igual

      return {
        branchId: Number(branch.id),
        branchName: branch.name,
        totalDailyPayments: totalDailyPaymentsSum,
        totalIncentives: totalIncentivesSum,
        totalDeductions: totalDeductionsSum,
        totalMissingBoxDeductions: totalMissingBoxDeductionsSum,
        uniformDeduction: uniformDeductionSum,
        totalAuditoria: totalAuditoriaSum,
        totalPrestamos: totalPrestamosSum,
        totalOtros: totalOtrosSum,
        totalPayWithIncentives: totalPayWithIncentivesSum,
        totalCollaborators: mappedRecords.length,
        collaboratorTotals,
        loading: false,
      };
    } catch (error) {
      if (isPayrollFetchAborted(error)) throw error;
      console.error(`Error calculating total for branch ${branch.id}:`, error);
      return {
        branchId: Number(branch.id),
        branchName: branch.name,
        totalDailyPayments: 0,
        totalIncentives: 0,
        totalDeductions: 0,
        totalMissingBoxDeductions: 0,
        uniformDeduction: 0,
        totalAuditoria: 0,
        totalPrestamos: 0,
        totalOtros: 0,
        totalPayWithIncentives: 0,
        totalCollaborators: 0,
        collaboratorTotals: [],
        loading: false,
      };
    }
  };

  // Calcular totales para todas las sucursales seleccionadas
  React.useEffect(() => {
    if (selectedBranch.length === 0) {
      lastDatesRef.current = "";
      setBranchesTotals(new Map());
      setLoadingBranches(new Set());
      return;
    }

    const ac = new AbortController();

    const calculateNewBranches = async () => {
      try {
        const currentDatesKey = `${dates.start.format("YYYY-MM-DD")}_${dates.end.format("YYYY-MM-DD")}`;
        const datesChanged =
          lastDatesRef.current !== "" && lastDatesRef.current !== currentDatesKey;

        if (datesChanged || lastDatesRef.current === "") {
          lastDatesRef.current = currentDatesKey;

          const newTotals = new Map<number, BranchTotal>();
          const loadingSet = new Set<number>();

          // Marcar todas las sucursales como loading
          selectedBranch.forEach((branch) => {
            const branchId = Number(branch.id);
            loadingSet.add(branchId);
            newTotals.set(branchId, {
              branchId,
              branchName: branch.name,
              totalDailyPayments: 0,
              totalIncentives: 0,
              totalDeductions: 0,
              totalMissingBoxDeductions: 0,
              uniformDeduction: 0,
              totalAuditoria: 0,
              totalPrestamos: 0,
              totalOtros: 0,
              totalPayWithIncentives: 0,
              totalCollaborators: 0,
              collaboratorTotals: [],
              loading: true,
            });
          });
          if (ac.signal.aborted) return;
          setBranchesTotals(newTotals);
          setLoadingBranches(new Set(loadingSet));

          // Calcular todas las sucursales en paralelo
          const calculations = await Promise.all(
            selectedBranch.map(async (branch) => {
              const branchId = Number(branch.id);
              try {
                const total = await calculateBranchTotal(branch, ac.signal);
                return { branchId, total };
              } catch (error) {
                if (isPayrollFetchAborted(error)) throw error;
                console.error(`Error calculating branch ${branch.name}:`, error);
                return {
                  branchId,
                  total: {
                    branchId,
                    branchName: branch.name,
                    totalDailyPayments: 0,
                    totalIncentives: 0,
                    totalDeductions: 0,
                    totalMissingBoxDeductions: 0,
                    uniformDeduction: 0,
                    totalAuditoria: 0,
                    totalPrestamos: 0,
                    totalOtros: 0,
                    totalPayWithIncentives: 0,
                    totalCollaborators: 0,
                    collaboratorTotals: [],
                    loading: false,
                  },
                };
              }
            })
          );

          if (ac.signal.aborted) return;

          calculations.forEach(({ branchId, total }) => {
            newTotals.set(branchId, total);
          });

          setBranchesTotals(newTotals);
          setLoadingBranches(new Set());
          return;
        }

        // Si solo cambió la lista de sucursales, solo fetchear las nuevas
        // Copiar las sucursales ya procesadas
        const currentTotals = new Map(branchesTotals);
        const currentLoading = new Set(loadingBranches);

        // Sucursales a calcular: ausentes, o presentes pero aún en loading (p. ej. Strict Mode / abort)
        const newBranches = selectedBranch.filter((branch) => {
          const branchId = Number(branch.id);
          if (currentLoading.has(branchId)) return false;
          const existing = currentTotals.get(branchId);
          if (!existing) return true;
          if (existing.loading === true) return true;
          return false;
        });

        // Identificar sucursales que fueron removidas
        const selectedBranchIds = new Set(selectedBranch.map((b) => Number(b.id)));
        const removedBranches: number[] = [];
        currentTotals.forEach((_, branchId) => {
          if (!selectedBranchIds.has(branchId)) {
            removedBranches.push(branchId);
          }
        });
        currentLoading.forEach((branchId) => {
          if (!selectedBranchIds.has(branchId)) {
            removedBranches.push(branchId);
          }
        });

        // Remover sucursales que ya no están seleccionadas
        removedBranches.forEach((branchId) => {
          currentTotals.delete(branchId);
          currentLoading.delete(branchId);
        });

        // Si no hay nuevas sucursales y no se removieron, no hacer nada
        if (newBranches.length === 0 && removedBranches.length === 0) {
          return;
        }

        // Actualizar estado con sucursales removidas
        if (removedBranches.length > 0) {
          if (ac.signal.aborted) return;
          setBranchesTotals(new Map(currentTotals));
          setLoadingBranches(new Set(currentLoading));
        }

        // Si no hay nuevas sucursales para procesar, terminar
        if (newBranches.length === 0) {
          return;
        }

        // Marcar solo las nuevas sucursales como loading
        newBranches.forEach((branch) => {
          const branchId = Number(branch.id);
          currentLoading.add(branchId);
          currentTotals.set(branchId, {
            branchId,
            branchName: branch.name,
            totalDailyPayments: 0,
            totalIncentives: 0,
            totalDeductions: 0,
            totalMissingBoxDeductions: 0,
            uniformDeduction: 0,
            totalAuditoria: 0,
            totalPrestamos: 0,
            totalOtros: 0,
            totalPayWithIncentives: 0,
            totalCollaborators: 0,
            collaboratorTotals: [],
            loading: true,
          });
        });
        if (ac.signal.aborted) return;
        setBranchesTotals(new Map(currentTotals));
        setLoadingBranches(new Set(currentLoading));

        // Calcular solo las nuevas sucursales en paralelo
        const calculations = await Promise.all(
          newBranches.map(async (branch) => {
            const branchId = Number(branch.id);
            try {
              const total = await calculateBranchTotal(branch, ac.signal);
              return { branchId, total };
            } catch (error) {
              if (isPayrollFetchAborted(error)) throw error;
              console.error(`Error calculating branch ${branch.name}:`, error);
              return {
                branchId,
                total: {
                  branchId,
                  branchName: branch.name,
                  totalDailyPayments: 0,
                  totalIncentives: 0,
                  totalDeductions: 0,
                  totalMissingBoxDeductions: 0,
                  uniformDeduction: 0,
                  totalAuditoria: 0,
                  totalPrestamos: 0,
                  totalOtros: 0,
                  totalPayWithIncentives: 0,
                  totalCollaborators: 0,
                  collaboratorTotals: [],
                  loading: false,
                },
              };
            }
          })
        );

        if (ac.signal.aborted) return;

        // Actualizar solo las nuevas sucursales
        calculations.forEach(({ branchId, total }) => {
          currentTotals.set(branchId, total);
          currentLoading.delete(branchId);
        });

        setBranchesTotals(new Map(currentTotals));
        setLoadingBranches(new Set(currentLoading));
      } catch (e) {
        if (isPayrollFetchAborted(e)) return;
        console.error("Error en cálculo de resumen de nómina:", e);
      }
    };

    calculateNewBranches();
    return () => ac.abort();
  }, [
    selectedBranch.map((b) => b.id).join(","),
    dates.start.format("YYYY-MM-DD"),
    dates.end.format("YYYY-MM-DD"),
  ]);

  const grandTotal = React.useMemo(() => {
    let totalDailyPayments = 0;
    let totalIncentives = 0;
    let totalDeductions = 0;
    let totalMissingBoxDeductions = 0;
    let uniformDeduction = 0;
    let totalAuditoria = 0;
    let totalPrestamos = 0;
    let totalOtros = 0;
    let totalPayWithIncentives = 0;
    let totalCollaborators = 0;

    selectedBranch.forEach((b) => {
      const branchTotal = branchesTotals.get(Number(b.id));
      if (!branchTotal || branchTotal.loading) {
        return;
      }
      totalDailyPayments += branchTotal.totalDailyPayments || 0;
      totalIncentives += branchTotal.totalIncentives || 0;
      totalDeductions += branchTotal.totalDeductions || 0;
      totalMissingBoxDeductions += branchTotal.totalMissingBoxDeductions || 0;
      uniformDeduction += branchTotal.uniformDeduction || 0;
      totalAuditoria += branchTotal.totalAuditoria || 0;
      totalPrestamos += branchTotal.totalPrestamos || 0;
      totalOtros += branchTotal.totalOtros || 0;
      totalPayWithIncentives += branchTotal.totalPayWithIncentives || 0;
      totalCollaborators += branchTotal.totalCollaborators || 0;
    });

    return {
      totalDailyPayments,
      totalIncentives,
      totalDeductions,
      totalMissingBoxDeductions,
      uniformDeduction,
      totalAuditoria,
      totalPrestamos,
      totalOtros,
      totalPayWithIncentives,
      totalCollaborators,
    };
  }, [branchesTotals, selectedBranch]);

  const exportToExcel = React.useCallback(() => {
    if (branchesTotals.size === 0) {
      notification("Aviso", "No hay datos para exportar", "warning");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const startDate = dates.start.format("DD-MM-YYYY");
    const endDate = dates.end.format("DD-MM-YYYY");

    const data: any[][] = [];

    data.push(["PAGO ESTIMADO TOTAL POR SUCURSAL"]);
    data.push([]);
    data.push([
      "PERIODO:",
      `${startDate} al ${endDate}`,
      "",
      "",
    ]);
    data.push([]);

    const headers = [
      "Sucursal",
      "Total Colaboradores",
      "Total Pagos Diarios",
      "Total Incentivos",
      "Total Deducciones",
      "Faltantes en Caja",
      "Deducción Uniforme",
      "Auditoría",
      "Préstamos",
      "Otros",
      "Pago Estimado Total",
    ];
    data.push(headers);

    branchesTotals.forEach((branchTotal) => {
      const row = [
        branchTotal.branchName,
        branchTotal.totalCollaborators,
        getCurrencyFormat(branchTotal.totalDailyPayments),
        getCurrencyFormat(branchTotal.totalIncentives),
        getCurrencyFormat(branchTotal.totalDeductions),
        getCurrencyFormat(branchTotal.totalMissingBoxDeductions),
        getCurrencyFormat(branchTotal.uniformDeduction),
        getCurrencyFormat(branchTotal.totalAuditoria || 0),
        getCurrencyFormat(branchTotal.totalPrestamos || 0),
        getCurrencyFormat(branchTotal.totalOtros || 0),
        getCurrencyFormat(branchTotal.totalPayWithIncentives),
      ];
      data.push(row);
    });

    data.push([]);
    const totalsRow = [
      "TOTAL GENERAL",
      grandTotal.totalCollaborators,
      getCurrencyFormat(grandTotal.totalDailyPayments),
      getCurrencyFormat(grandTotal.totalIncentives),
      getCurrencyFormat(grandTotal.totalDeductions),
      getCurrencyFormat(grandTotal.totalMissingBoxDeductions),
      getCurrencyFormat(grandTotal.uniformDeduction),
      getCurrencyFormat(grandTotal.totalAuditoria || 0),
      getCurrencyFormat(grandTotal.totalPrestamos || 0),
      getCurrencyFormat(grandTotal.totalOtros || 0),
      getCurrencyFormat(grandTotal.totalPayWithIncentives),
    ];
    data.push(totalsRow);

    const worksheet = XLSX.utils.aoa_to_sheet(data);

    const titleStyle = {
      font: { bold: true, sz: 18, color: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    const headerStyle = {
      font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FFFFFF" } },
        bottom: { style: "thin", color: { rgb: "FFFFFF" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    const dataStyle = {
      font: { sz: 10, color: { rgb: "374151" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } },
      },
    };

    const totalsStyle = {
      font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "50B7C0" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "medium", color: { rgb: "1F4E79" } },
        bottom: { style: "medium", color: { rgb: "1F4E79" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };

    if (worksheet["A1"]) worksheet["A1"].s = titleStyle;

    // Estilos para headers
    const headerRowIndex = 4; // Después de los títulos
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: i });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = headerStyle;
      }
    }

    // Estilos para datos
    const dataStartRow = headerRowIndex + 1;
    const dataEndRow = dataStartRow + branchesTotals.size;
    for (let row = dataStartRow; row < dataEndRow; row++) {
      const isAlt = (row - dataStartRow) % 2 === 1;
      for (let col = 0; col < headers.length; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            ...dataStyle,
            fill: isAlt ? { fgColor: { rgb: "F9FAFB" } } : undefined,
          };
        }
      }
    }

    // Estilos para totales
    const totalsRowIndex = dataEndRow + 1;
    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: totalsRowIndex, c: col });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = totalsStyle;
      }
    }

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 2, c: 1 }, e: { r: 2, c: headers.length - 1 } },
    ];

    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Pago Estimado Total");

    const fileName = `Pago_Estimado_Total_${startDate.replace(/\//g, "-")}_${endDate.replace(/\//g, "-")}.xlsx`;

    XLSX.writeFile(workbook, fileName);
    notification("Éxito", "Excel descargado correctamente", "success");
  }, [branchesTotals, dates, grandTotal]);

  const selectedBranchTotal = React.useMemo(() => {
    if (selectedBranchBreakdown === null) return null;
    return branchesTotals.get(selectedBranchBreakdown) || null;
  }, [selectedBranchBreakdown, branchesTotals]);

  const denominationRows = React.useMemo(() => {
    if (!selectedBranchTotal?.collaboratorTotals?.length) return [];
    return selectedBranchTotal.collaboratorTotals.map((collab) => ({
      ...collab,
      breakdown: calculateDenominationBreakdown(collab.totalPayWithIncentives),
    }));
  }, [selectedBranchTotal]);

  const denominationTotals = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const amounts: Record<string, number> = {};
    DENOMINATIONS.forEach((denomination) => {
      const key = toDenominationKey(denomination);
      counts[key] = 0;
      amounts[key] = 0;
    });

    let totalRoundedAmount = 0;
    denominationRows.forEach((row) => {
      totalRoundedAmount += row.breakdown.roundedAmount;
      DENOMINATIONS.forEach((denomination) => {
        const key = toDenominationKey(denomination);
        const count = row.breakdown.counts[key] || 0;
        counts[key] += count;
        amounts[key] += count * denomination;
      });
    });

    return { counts, amounts, totalRoundedAmount };
  }, [denominationRows]);

  const filteredDenominationRows = React.useMemo(() => {
    const term = normalizeSearchText(denominationSearch);
    if (!term) return denominationRows;
    return denominationRows.filter((row) =>
      normalizeSearchText(row.name).includes(term)
    );
  }, [denominationRows, denominationSearch]);

  const filteredEditableRows = React.useMemo(() => {
    return filteredDenominationRows.map((row) => {
      const counts = editableCountsByPartner.get(row.partnerId) || row.breakdown.counts;
      const assignedAmount = calculateAssignedAmount(counts);
      return {
        ...row,
        counts,
        assignedAmount,
        differenceAmount: Number((row.breakdown.roundedAmount - assignedAmount).toFixed(2)),
      };
    });
  }, [filteredDenominationRows, editableCountsByPartner]);

  const filteredDenominationTotals = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const amounts: Record<string, number> = {};
    DENOMINATIONS.forEach((denomination) => {
      const key = toDenominationKey(denomination);
      counts[key] = 0;
      amounts[key] = 0;
    });

    let totalRoundedAmount = 0;
    filteredEditableRows.forEach((row) => {
      totalRoundedAmount += row.assignedAmount;
      DENOMINATIONS.forEach((denomination) => {
        const key = toDenominationKey(denomination);
        const count = row.counts[key] || 0;
        counts[key] += count;
        amounts[key] += count * denomination;
      });
    });

    return { counts, amounts, totalRoundedAmount };
  }, [filteredEditableRows]);

  React.useEffect(() => {
    if (selectedBranchBreakdown === null) {
      setEditableCountsByPartner(new Map());
      setLastEditedFieldByPartner(new Map());
      setDirtyPartnerIds(new Set());
      setIsSavingAllEdits(false);
      return;
    }

    const initialCounts = new Map<number, DenominationCounts>();
    denominationRows.forEach((row) => {
      initialCounts.set(row.partnerId, { ...row.breakdown.counts });
    });
    setEditableCountsByPartner(initialCounts);
    setLastEditedFieldByPartner(new Map());
    setDirtyPartnerIds(new Set());
  }, [selectedBranchBreakdown, denominationRows]);

  React.useEffect(() => {
    const loadSavedBreakdowns = async () => {
      if (selectedBranchBreakdown === null) return;
      try {
        const start = dates.start.format("YYYY-MM-DD");
        const end = dates.end.format("YYYY-MM-DD");
        const response = await findPayrollDenominationBreakdown(
          selectedBranchBreakdown,
          start,
          end
        );
        const rows = response?.rows || [];
        if (!rows.length) return;

        setEditableCountsByPartner((prev) => {
          const next = new Map(prev);
          rows.forEach((row) => {
            if (next.has(row.partnerId) && row.counts) {
              next.set(row.partnerId, { ...next.get(row.partnerId), ...row.counts });
            }
          });
          return next;
        });
      } catch (error) {
        console.error("Error cargando desglose guardado:", error);
      }
    };
    loadSavedBreakdowns();
  }, [
    selectedBranchBreakdown,
    dates.start,
    dates.end,
  ]);

  const handleDenominationEdit = React.useCallback(
    (partnerId: number, denomination: number, nextRawValue: string) => {
      const denominationKey = toDenominationKey(denomination);
      const normalizedValue = normalizeCountInput(nextRawValue);
      const safeValue = Math.max(0, parseInt(normalizedValue, 10) || 0);

      setEditableCountsByPartner((prev) => {
        const next = new Map(prev);
        const currentRow = next.get(partnerId);
        if (!currentRow) return prev;

        const rowData = denominationRows.find((r) => r.partnerId === partnerId);
        if (!rowData) return prev;

        const rowCounts: DenominationCounts = { ...currentRow };
        const currentAtField = rowCounts[denominationKey] || 0;
        const currentAssigned = calculateAssignedAmount(rowCounts);
        const otherAssigned = currentAssigned - currentAtField * denomination;
        const maxAllowed = Math.max(
          0,
          Math.floor((rowData.breakdown.roundedAmount - otherAssigned) / denomination)
        );
        rowCounts[denominationKey] = Math.min(safeValue, maxAllowed);
        next.set(partnerId, rowCounts);
        return next;
      });

      setLastEditedFieldByPartner((prev) => {
        const next = new Map(prev);
        next.set(partnerId, denominationKey);
        return next;
      });
      setDirtyPartnerIds((prev) => {
        const next = new Set(prev);
        next.add(partnerId);
        return next;
      });
    },
    [denominationRows]
  );

  const handleSaveAllEdits = React.useCallback(async () => {
    if (selectedBranchBreakdown === null || dirtyPartnerIds.size === 0) return;
    setIsSavingAllEdits(true);
    try {
      const tasks = Array.from(dirtyPartnerIds).map(async (partnerId) => {
        const editedField = lastEditedFieldByPartner.get(partnerId);
        const currentCounts = editableCountsByPartner.get(partnerId);
        if (!editedField || !currentCounts) return;
        const editedCount = Number(currentCounts[editedField] || 0);
        await upsertPayrollDenominationBreakdown({
          partner: partnerId,
          branch: selectedBranchBreakdown,
          periodStart: dates.start.format("YYYY-MM-DD"),
          periodEnd: dates.end.format("YYYY-MM-DD"),
          counts: currentCounts,
          editedDenomination: editedField,
          editedCount,
        });
      });
      await Promise.all(tasks);
      setDirtyPartnerIds(new Set());
      notification("Guardado", "Cambios de denominaciones guardados", "success");
    } catch (error) {
      console.error("Error guardando desglose:", error);
      notification("Error", "No se pudieron guardar algunos cambios", "danger");
    } finally {
      setIsSavingAllEdits(false);
    }
  }, [
    selectedBranchBreakdown,
    dirtyPartnerIds,
    lastEditedFieldByPartner,
    editableCountsByPartner,
    dates.start,
    dates.end,
  ]);

  const exportDenominationBreakdownExcel = React.useCallback(() => {
    if (selectedBranchBreakdown === null || filteredEditableRows.length === 0) {
      notification("Aviso", "No hay datos para exportar", "warning");
      return;
    }

    const branchName = selectedBranchTotal?.branchName || "Sucursal";
    const periodStart = dates.start.format("DD-MM-YYYY");
    const periodEnd = dates.end.format("DD-MM-YYYY");
    const filterLabel = denominationSearch.trim()
      ? `Búsqueda: "${denominationSearch.trim()}"`
      : "Sin filtro de búsqueda";

    const headers = [
      "Colaborador",
      "Pago asignado",
      ...DENOMINATIONS.map((d) => `$${d}`),
    ];

    const data: (string | number)[][] = [];
    data.push(["DESGLOSE DE DENOMINACIONES"]);
    data.push([]);
    data.push(["SUCURSAL:", branchName, "", "PERIODO:", `${periodStart} al ${periodEnd}`]);
    data.push([filterLabel]);
    data.push(["Colaboradores en vista:", filteredEditableRows.length]);
    data.push([]);
    data.push(headers);

    filteredEditableRows.forEach((row) => {
      data.push([
        toDisplayName(row.name),
        getCurrencyFormat(row.assignedAmount),
        ...DENOMINATIONS.map((denomination) => {
          const key = toDenominationKey(denomination);
          return row.counts[key] || 0;
        }),
      ]);
    });

    data.push([]);
    data.push([
      "RESUMEN",
      getCurrencyFormat(filteredDenominationTotals.totalRoundedAmount),
      ...DENOMINATIONS.map((denomination) => {
        const key = toDenominationKey(denomination);
        return filteredDenominationTotals.counts[key] || 0;
      }),
    ]);
    data.push([
      "IMPORTE POR DENOMINACIÓN",
      "",
      ...DENOMINATIONS.map((denomination) => {
        const key = toDenominationKey(denomination);
        return getCurrencyFormat(filteredDenominationTotals.amounts[key] || 0);
      }),
    ]);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(data);

    const titleStyle = {
      font: { bold: true, sz: 18, color: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    const infoStyle = {
      font: { bold: true, sz: 11, color: { rgb: "374151" } },
      alignment: { horizontal: "left", vertical: "center" },
    };
    const infoValueStyle = {
      font: { sz: 11, color: { rgb: "1F4E79" } },
      alignment: { horizontal: "left", vertical: "center" },
    };
    const headerStyle = {
      font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "50B7C0" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FFFFFF" } },
        bottom: { style: "thin", color: { rgb: "FFFFFF" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };
    const dataStyle = {
      font: { sz: 10, color: { rgb: "374151" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "E5E7EB" } },
        bottom: { style: "thin", color: { rgb: "E5E7EB" } },
        left: { style: "thin", color: { rgb: "E5E7EB" } },
        right: { style: "thin", color: { rgb: "E5E7EB" } },
      },
    };
    const nameStyle = {
      ...dataStyle,
      alignment: { horizontal: "left", vertical: "center" },
      font: { sz: 10, color: { rgb: "1F4E79" }, bold: true },
    };
    const totalsStyle = {
      font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F4E79" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "medium", color: { rgb: "1F4E79" } },
        bottom: { style: "medium", color: { rgb: "1F4E79" } },
        left: { style: "thin", color: { rgb: "FFFFFF" } },
        right: { style: "thin", color: { rgb: "FFFFFF" } },
      },
    };
    const totalsTitleStyle = {
      ...totalsStyle,
      alignment: { horizontal: "left", vertical: "center" },
    };

    const colCount = headers.length;
    if (worksheet["A1"]) worksheet["A1"].s = titleStyle;
    if (worksheet["A3"]) worksheet["A3"].s = infoStyle;
    if (worksheet["B3"]) worksheet["B3"].s = infoValueStyle;
    if (worksheet["D3"]) worksheet["D3"].s = infoStyle;
    if (worksheet["E3"]) worksheet["E3"].s = infoValueStyle;
    if (worksheet["A4"]) worksheet["A4"].s = infoStyle;
    if (worksheet["A5"]) worksheet["A5"].s = infoStyle;
    if (worksheet["B5"]) worksheet["B5"].s = infoValueStyle;

    const headerRowIndex = 6;
    for (let i = 0; i < colCount; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: headerRowIndex, c: i });
      if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
    }

    const dataStartRow = headerRowIndex + 1;
    const dataEndRow = dataStartRow + filteredEditableRows.length;
    for (let row = dataStartRow; row < dataEndRow; row++) {
      const isAlt = (row - dataStartRow) % 2 === 1;
      for (let col = 0; col < colCount; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellRef]) {
          if (col === 0) {
            worksheet[cellRef].s = {
              ...nameStyle,
              fill: isAlt ? { fgColor: { rgb: "F9FAFB" } } : undefined,
            };
          } else {
            worksheet[cellRef].s = {
              ...dataStyle,
              fill: isAlt ? { fgColor: { rgb: "F9FAFB" } } : undefined,
            };
          }
        }
      }
    }

    const summaryRowIndex = dataEndRow + 1;
    const importRowIndex = summaryRowIndex + 1;
    for (let col = 0; col < colCount; col++) {
      const ref1 = XLSX.utils.encode_cell({ r: summaryRowIndex, c: col });
      if (worksheet[ref1]) {
        worksheet[ref1].s = col === 0 ? totalsTitleStyle : totalsStyle;
      }
      const ref2 = XLSX.utils.encode_cell({ r: importRowIndex, c: col });
      if (worksheet[ref2]) {
        worksheet[ref2].s = col === 0 ? totalsTitleStyle : totalsStyle;
      }
    }

    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
    worksheet["!cols"] = [
      { wch: 34 },
      { wch: 16 },
      ...DENOMINATIONS.map(() => ({ wch: 10 })),
    ];

    XLSX.utils.book_append_sheet(workbook, worksheet, "Desglose denominaciones");
    const safeBranch = branchName.replace(/\s+/g, "_");
    const fileName = `Desglose_Denominaciones_${safeBranch}_${periodStart}_${periodEnd}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    notification("Éxito", "Excel descargado correctamente", "success");
  }, [
    selectedBranchBreakdown,
    selectedBranchTotal,
    dates.start,
    dates.end,
    denominationSearch,
    filteredEditableRows,
    filteredDenominationTotals,
  ]);

  // Exponer la función exportToExcel al componente padre cuando esté lista
  React.useEffect(() => {
    if (onExportExcel && loadingBranches.size === 0 && branchesTotals.size > 0) {
      onExportExcel(exportToExcel);
    }
  }, [exportToExcel, onExportExcel, loadingBranches.size, branchesTotals.size]);

  if (selectedBranch.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <div className="d-flex flex-column align-items-center justify-content-center">
          <EmptyImage className="mx-auto w-250px" />
          <h5 className="text-secondary text-center fs-20px mt-3">
            Sin sucursales seleccionadas
          </h5>
        </div>
      </div>
    );
  }

  // Componente Skeleton para las celdas
  const SkeletonCell = ({ width = "80px" }: { width?: string }) => (
    <div
      style={{
        height: "20px",
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "loading 1.5s ease-in-out infinite",
        borderRadius: "4px",
        width,
        marginLeft: "auto",
      }}
    />
  );

  // Componente Skeleton para las filas
  // Total de 4 columnas: Sucursal, Colaboradores, Pagos Diarios, Otros (Total), Pago Estimado Total
  const SkeletonRow = ({ branchName }: { branchName: string }) => (
    <tr style={{ background: "#ffffff", borderBottom: "1px solid #e5e7eb" }}>
      <td
        style={{
          padding: "12px 16px",
          fontWeight: 600,
          color: "#1F4E79",
        }}
      >
        {branchName}
      </td>
      <td style={{ padding: "12px 16px", textAlign: "center" }}>
        <SkeletonCell width="60px" />
      </td>
      {/* 3 columnas más: Pagos Diarios, Otros (Total), Pago Estimado Total */}
      {[1, 2, 3].map((_, idx) => (
        <td key={idx} style={{ padding: "12px 16px", textAlign: "right" }}>
          <SkeletonCell width={idx === 2 ? "100px" : "80px"} />
        </td>
      ))}
    </tr>
  );

  return (
    <div>
      <style>
        {`
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
      {/* Tabla de pago estimado por sucursal */}
      {(branchesTotals.size > 0 || loadingBranches.size > 0) && (
        <div
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.85rem",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "linear-gradient(135deg, #1F4E79 0%, #2F5597 100%)",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "left",
                      borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Sucursal
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "center",
                      borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Colaboradores
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "right",
                      borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Pagos Diarios
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "right",
                      borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Otros 
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "right",
                      borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                    }}
                  >
                    Pago Estimado Total
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      color: "#ffffff",
                      fontWeight: 700,
                      textAlign: "center",
                    }}
                  >
                    Desglose
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedBranch.map((branch, index) => {
                  const branchId = Number(branch.id);
                  const branchTotal = branchesTotals.get(branchId);
                  const isLoading = loadingBranches.has(branchId);

                  // Si está cargando, mostrar skeleton
                  if (isLoading) {
                    return <SkeletonRow key={branchId} branchName={branch.name} />;
                  }

                  // Si no hay datos aún, no mostrar nada (no debería pasar)
                  if (!branchTotal) {
                    return null;
                  }

                  // Mostrar datos reales
                  return (
                    <tr
                      key={branchId}
                      style={{
                        background: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <td
                        style={{
                          padding: "12px 16px",
                          fontWeight: 600,
                          color: "#1F4E79",
                        }}
                      >
                        {branchTotal.branchName}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        {branchTotal.totalCollaborators}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {getCurrencyFormat(branchTotal.totalDailyPayments)}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          textAlign: "right",
                          color: "#000000",
                          fontWeight: 600,
                        }}
                      >
                        {(() => {
                          // Sumar todos: Incentivos (positivo), Deducciones, Faltantes, Uniforme, Auditoría, Préstamos, Otros (todos negativos)
                          const totalOtros =
                            branchTotal.totalIncentives -
                            (branchTotal.totalDeductions +
                              branchTotal.totalMissingBoxDeductions +
                              branchTotal.uniformDeduction +
                              (branchTotal.totalAuditoria || 0) +
                              (branchTotal.totalPrestamos || 0) +
                              (branchTotal.totalOtros || 0));
                          return totalOtros >= 0
                            ? `+${getCurrencyFormat(totalOtros)}`
                            : getCurrencyFormat(totalOtros);
                        })()}
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          textAlign: "right",
                          fontWeight: 700,
                          color: "#1F4E79",
                          fontSize: "0.9rem",
                        }}
                      >
                        {getCurrencyFormat(branchTotal.totalPayWithIncentives)}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <button
                          type="button"
                          className="border-0 d-inline-flex align-items-center"
                          onClick={() => setSelectedBranchBreakdown(branchId)}
                          style={{
                            background:
                              "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
                            color: "#ffffff",
                            borderRadius: "12px",
                            padding: "0.35rem 0.75rem",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <i className="fas fa-coins me-1" style={{ fontSize: "0.7rem" }}></i>
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr
                  style={{
                    background: "linear-gradient(135deg, #50B7C0 0%, #3a9ea6 100%)",
                    borderTop: "2px solid #1F4E79",
                  }}
                >
                  <td
                    style={{
                      padding: "14px 16px",
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "0.95rem",
                    }}
                  >
                    TOTAL GENERAL
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "0.95rem",
                    }}
                  >
                    {grandTotal.totalCollaborators}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "0.95rem",
                    }}
                  >
                    {getCurrencyFormat(grandTotal.totalDailyPayments)}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "0.95rem",
                    }}
                  >
                    {(() => {
                      // Sumar todos: Incentivos (positivo), Deducciones, Faltantes, Uniforme, Auditoría, Préstamos, Otros (todos negativos)
                      const totalOtros = 
                        grandTotal.totalIncentives -
                        (grandTotal.totalDeductions +
                         grandTotal.totalMissingBoxDeductions +
                         grandTotal.uniformDeduction +
                         (grandTotal.totalAuditoria || 0) +
                         (grandTotal.totalPrestamos || 0) +
                         (grandTotal.totalOtros || 0));
                      return totalOtros >= 0 
                        ? `+${getCurrencyFormat(totalOtros)}`
                        : getCurrencyFormat(totalOtros);
                    })()}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      textAlign: "right",
                      fontWeight: 700,
                      color: "#ffffff",
                      fontSize: "1.1rem",
                    }}
                  >
                    {getCurrencyFormat(grandTotal.totalPayWithIncentives)}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                    }}
                  ></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {loadingBranches.size === 0 && branchesTotals.size === 0 && (
        <div className="d-flex align-items-center justify-content-center">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <EmptyImage className="mx-auto w-250px" />
            <h5 className="text-secondary text-center fs-20px mt-3">
              No hay datos disponibles
            </h5>
          </div>
        </div>
      )}

      <ModalContainer
        open={selectedBranchBreakdown !== null}
        closeFn={() => {
          setSelectedBranchBreakdown(null);
          setDenominationSearch("");
          setIsSavingAllEdits(false);
        }}
        title={`Desglose de denominaciones${
          selectedBranchTotal ? ` - ${selectedBranchTotal.branchName}` : ""
        }`}
        size="xl"
      >
        <div style={{ maxHeight: "75vh", overflow: "hidden" }}>
          {denominationRows.length === 0 ? (
            <div
              className="text-center py-4 text-secondary"
              style={{
                background: "#f8fafc",
                borderRadius: "14px",
                border: "1px dashed #cbd5e1",
              }}
            >
              No hay colaboradores disponibles para desglose.
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "0.9rem",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ position: "relative", minWidth: "280px", flex: 1 }}>
                  <i
                    className="fas fa-search"
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                      fontSize: "0.8rem",
                    }}
                  ></i>
                  <input
                    type="text"
                    value={denominationSearch}
                    onChange={(e) => setDenominationSearch(e.target.value)}
                    placeholder="Buscar colaborador..."
                    style={{
                      width: "100%",
                      border: "1px solid #cbd5e1",
                      borderRadius: "12px",
                      padding: "0.5rem 0.75rem 0.5rem 2rem",
                      fontSize: "0.85rem",
                      outline: "none",
                    }}
                  />
                </div>
                <div style={{ color: "#334155", fontSize: "0.8rem", fontWeight: 600 }}>
                  Mostrando {filteredDenominationRows.length} de {denominationRows.length}
                </div>
                <div className="d-flex align-items-center" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    onClick={exportDenominationBreakdownExcel}
                    disabled={filteredEditableRows.length === 0}
                    style={{
                      border: "none",
                      borderRadius: "10px",
                      padding: "0.45rem 0.8rem",
                      background:
                        filteredEditableRows.length > 0
                          ? "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)"
                          : "#d1d5db",
                      color: "#ffffff",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      cursor: filteredEditableRows.length > 0 ? "pointer" : "not-allowed",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <i className="fas fa-file-excel me-1" style={{ fontSize: "0.7rem" }}></i>
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAllEdits}
                    disabled={dirtyPartnerIds.size === 0 || isSavingAllEdits}
                    style={{
                      border: "none",
                      borderRadius: "10px",
                      padding: "0.45rem 0.8rem",
                      background:
                        dirtyPartnerIds.size > 0 && !isSavingAllEdits
                          ? "#111827"
                          : "#d1d5db",
                      color: "#ffffff",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      cursor:
                        dirtyPartnerIds.size > 0 && !isSavingAllEdits
                          ? "pointer"
                          : "not-allowed",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isSavingAllEdits ? "Guardando..." : "Guardar cambios"}
                  </button>
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "16px",
                  overflow: "auto",
                  maxHeight: "62vh",
                  boxShadow:
                    "0 8px 24px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.5)",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.55rem 0.8rem",
                    background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
                    borderBottom: "1px solid #d1d5db",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  <span>Billetes y monedas por colaborador</span>
                  <span>
                    Total filtrado: {getCurrencyFormat(filteredDenominationTotals.totalRoundedAmount)}
                  </span>
                </div>
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr
                      style={{
                        background: "linear-gradient(135deg, #334155 0%, #1f2937 100%)",
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                      }}
                    >
                      <th
                        style={{
                          minWidth: "260px",
                          color: "#f8fafc",
                          padding: "0.78rem 0.7rem",
                          borderBottom: "1px solid rgba(255,255,255,0.15)",
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          background: "linear-gradient(135deg, #334155 0%, #1f2937 100%)",
                        }}
                      >
                        Colaborador
                      </th>
                      <th
                        className="text-end"
                        style={{
                          minWidth: "130px",
                          color: "#f8fafc",
                          padding: "0.78rem 0.7rem",
                          borderBottom: "1px solid rgba(255,255,255,0.15)",
                        }}
                      >
                        Pago
                      </th>
                      {DENOMINATIONS.map((denomination) => (
                        <th
                          key={denomination}
                          className="text-center"
                          style={{
                            minWidth: "74px",
                            color: "#f8fafc",
                            padding: "0.78rem 0.55rem",
                            borderBottom: "1px solid rgba(255,255,255,0.15)",
                          }}
                        >
                          <span
                            style={{
                              background: "#1e293b",
                              border: "1px solid #334155",
                              borderRadius: "999px",
                              padding: "0.1rem 0.5rem",
                              fontSize: "0.72rem",
                              fontWeight: 700,
                            }}
                          >
                            ${denomination}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEditableRows.map((row, idx) => (
                      <tr
                        key={`${row.partnerId}-${row.name}`}
                        style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc" }}
                      >
                        <td
                          style={{
                            fontWeight: 500,
                            color: "#334155",
                            padding: "0.78rem 0.6rem",
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            background: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                            borderRight: "1px solid #e2e8f0",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
                            <i
                              className="fas fa-user-circle"
                              style={{ color: "#6b7280", fontSize: "0.95rem" }}
                            ></i>
                            <span>{toDisplayName(row.name)}</span>
                          </div>
                        </td>
                        <td
                          className="text-end"
                          style={{
                            fontWeight: 700,
                            color: row.differenceAmount !== 0 ? "#dc2626" : "#374151",
                            padding: "0.78rem 0.6rem",
                            fontVariantNumeric: "tabular-nums",
                            fontSize: "1rem",
                          }}
                          title={
                            row.differenceAmount > 0
                              ? `Faltan ${getCurrencyFormat(row.differenceAmount)} por asignar`
                              : row.differenceAmount < 0
                              ? `Excede por ${getCurrencyFormat(Math.abs(row.differenceAmount))}`
                              : "Pago completo"
                          }
                        >
                          <div>{getCurrencyFormat(row.assignedAmount)}</div>
                          {row.differenceAmount !== 0 && (
                            <div
                              style={{
                                fontSize: "0.68rem",
                                fontWeight: 600,
                                color: "#dc2626",
                                marginTop: "0.08rem",
                              }}
                            >
                              {row.differenceAmount > 0
                                ? `Faltan ${getCurrencyFormat(row.differenceAmount)} de: ${getCurrencyFormat(
                                    row.breakdown.roundedAmount
                                  )}`
                                : `Excede ${getCurrencyFormat(Math.abs(row.differenceAmount))} de: ${getCurrencyFormat(
                                    row.breakdown.roundedAmount
                                  )}`}
                            </div>
                          )}
                        </td>
                        {DENOMINATIONS.map((denomination) => {
                          const key = toDenominationKey(denomination);
                          const value = row.counts[key] || 0;
                          return (
                            <td
                              key={key}
                              className="text-center"
                              style={{ padding: "0.78rem 0.5rem", fontVariantNumeric: "tabular-nums" }}
                            >
                              <input
                                type="text"
                                inputMode="numeric"
                                value={value}
                                onChange={(e) =>
                                  handleDenominationEdit(
                                    row.partnerId,
                                    denomination,
                                    e.target.value
                                  )
                                }
                                style={{
                                  width: "58px",
                                  textAlign: "center",
                                  borderRadius: "10px",
                                  border: value > 0 ? "1px solid #fcd34d" : "1px solid #d1d5db",
                                  background: value > 0 ? "#fef3c7" : "#ffffff",
                                  color: value > 0 ? "#92400e" : "#374151",
                                  fontWeight: value > 0 ? 700 : 500,
                                  padding: "0.15rem 0.25rem",
                                  fontSize: "0.8rem",
                                }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    <tr style={{ background: "#e5e7eb", fontWeight: 700 }}>
                      <td>Total pago redondeado</td>
                      <td className="text-end">
                        {getCurrencyFormat(filteredDenominationTotals.totalRoundedAmount)}
                      </td>
                      {DENOMINATIONS.map((denomination) => {
                        const key = toDenominationKey(denomination);
                        return (
                          <td key={key} className="text-center">
                            {filteredDenominationTotals.counts[key] || 0}
                          </td>
                        );
                      })}
                    </tr>
                    <tr style={{ background: "#f8fafc", fontWeight: 600 }}>
                      <td>Importe por denominación</td>
                      <td className="text-end">-</td>
                      {DENOMINATIONS.map((denomination) => {
                        const key = toDenominationKey(denomination);
                        return (
                          <td key={key} className="text-center">
                            {getCurrencyFormat(filteredDenominationTotals.amounts[key] || 0)}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default PaymentEstimadoTotal;
